import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, InputNumber, Button, Tag, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleFilled, SaveOutlined } from '@ant-design/icons';
import { blankLine, parseItemDesc, composeItemDesc } from './proformaHelpers';
import { itemsApi } from '../../masters/items/itemsApi';
import { LINE_TYPE, INTERLAYER_MASTER, composeLamiDesc } from './proformaNestingMasters';
import SmartItemDescCell from './SmartItemDescField';
import './proformaNestingModal.css';

// ════════════════════════════════════════════════════════════════════
// LAMI (laminated glass) config popup — glass + interlayer layers laid out as
// an Excel-style GRID (LAYER · ITEM DESC · RATE · FAB · SHAPE · TEMPLATE). On
// confirm it generates child rows (GLASS / INTERLAYER) and returns a parent
// patch (type LAMI, auto item-desc, rate = Σ layer prices).
//
// Each GLASS ply's ITEM DESC is the SAME single smart field as the proforma
// grid's ITEM DESC column (`SmartItemDescCell`) — one field for the glass
// Item-Master search AND the `#process@rate/uom` tags. Interlayers are picked
// from the seeded / DB master.
//
// SHAPE / TEMPLATE are per-glass capture-only flags (persisted in
// nesting_config); their business logic is defined in a later stage.
// ════════════════════════════════════════════════════════════════════

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const blankGlass = () => ({ layerType: 'GLASS', product: '', rawItemDesc: '', itemDesc: '', processPricing: [], price: null, fabApplicable: true, shape: false, template: false, itemId: null, stockItemId: null, stockItemName: null, materialTypeCode: null, categoryCode: null, glassTypeCode: null, thicknessMm: null });
const blankInter = () => ({ layerType: 'INTERLAYER', product: '', price: null });
const mmOf = (name) => { const m = String(name || '').match(/^([\d.]+)\s*MM/i); return m ? parseFloat(m[1]) : 0; };

// Rebuild a glass layer's derived fields (product/itemDesc/processPricing) from
// the single smart field's raw string — the SAME parse the grid uses.
function glassFromRaw(raw, prev) {
  const p = parseItemDesc(raw || '');
  return { ...prev, product: p.base, rawItemDesc: raw || '', itemDesc: composeItemDesc(p.base, p.tags), processPricing: p.pricing };
}

function defaultLayers(row) {
  // Re-editing an existing LAMI → restore the saved layer build-up.
  const saved = row && row.nestingConfig && row.nestingConfig.lami && Array.isArray(row.nestingConfig.lami.layers)
    ? row.nestingConfig.lami.layers : null;
  if (saved && saved.length >= 3) {
    return saved.map((l) => {
      const isGlass = String(l.layerType).toUpperCase() !== 'INTERLAYER';
      if (!isGlass) return { layerType: 'INTERLAYER', product: l.product || '', price: l.price != null ? l.price : null };
      // Prefer a saved raw string; fall back to (product + legacy #processDesc).
      const raw = l.rawItemDesc || (l.processDesc ? `${l.product || ''} ${l.processDesc}`.trim() : (l.product || ''));
      return glassFromRaw(raw, {
        layerType: 'GLASS',
        price: l.price != null ? l.price : null,
        fabApplicable: l.fabApplicable !== false,
        shape: l.shape === true,
        template: l.template === true,
        itemId: l.itemId || null,
        stockItemId: l.stockItemId || null,
        stockItemName: l.stockItemName || null,
        materialTypeCode: l.materialTypeCode || null,
        categoryCode: l.categoryCode || null,
        glassTypeCode: l.glassTypeCode || null,
        thicknessMm: l.thicknessMm != null ? l.thicknessMm : null
      });
    });
  }
  return [blankGlass(), blankInter(), blankGlass()];
}

// Stock-reference fields for a glass layer, derived from a picked Item-Master row.
function stockRefOf(item) {
  if (!item) return { itemId: null, stockItemId: null, stockItemName: null };
  return {
    itemId: item.id || null,
    stockItemId: item.sourceStockItemId || item.id || null,
    stockItemName: item.sourceStockItemName || item.itemName || item.itemDesc || null
  };
}

// Classification fields the Other-Charges rule engine matches on (carried from
// the picked Item-Master row + thickness parsed from the product name).
function classifyOf(item, productName) {
  const thk = mmOf(productName);
  return {
    materialTypeCode: (item && item.materialTypeCode) || null,
    categoryCode: (item && item.itemCategoryCode) || null,
    glassTypeCode: (item && item.glassTypeCode) || null,
    thicknessMm: thk > 0 ? thk : null
  };
}

export default function LamiConfigModal({ open, onClose, row, dropdowns, onConfirm }) {
  const [layers, setLayers] = useState(defaultLayers(null));
  useEffect(() => { if (open) setLayers(defaultLayers(row)); }, [open, row]);

  // Interlayer options are DB-driven from the PVB item master; the hardcoded
  // INTERLAYER_MASTER stays as a resilient fallback.
  const [interOptions, setInterOptions] = useState(
    () => INTERLAYER_MASTER.map((m) => ({ value: m.name, label: m.name, itemId: null })));
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    itemsApi.listInterlayers({ pageSize: 200 })
      .then((res) => {
        if (!alive) return;
        const rows = (res && res.rows) || [];
        if (rows.length) setInterOptions(rows.map((r) => ({ value: r.itemName, label: r.itemName, itemId: r.itemId || null })));
      })
      .catch(() => { /* keep the seeded fallback */ });
    return () => { alive = false; };
  }, [open]);

  const upd = (i, patch) => setLayers((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addPair = () => setLayers((p) => {
    const n = p.slice();
    if (n.length && n[n.length - 1].layerType === 'GLASS') n.push(blankInter());
    n.push(blankGlass());
    return n;
  });
  const removeAt = (i) => setLayers((p) => (p.length <= 3 ? p : p.filter((_l, j) => j !== i)));

  const glassCount = layers.filter((l) => l.layerType === 'GLASS').length;
  const interCount = layers.filter((l) => l.layerType === 'INTERLAYER').length;
  const errs = useMemo(() => {
    const e = [];
    for (let i = 0; i < layers.length - 1; i += 1) {
      if (layers[i].layerType === 'GLASS' && layers[i + 1].layerType === 'GLASS') e.push(`Interlayer missing between layer ${i + 1} & ${i + 2}`);
    }
    if (layers[0] && layers[0].layerType !== 'GLASS') e.push('First layer must be glass');
    if (layers.length && layers[layers.length - 1].layerType !== 'GLASS') e.push('Last layer must be glass');
    if (glassCount < 2) e.push('Min 2 glass layers');
    if (layers.some((l) => !l.product)) e.push('Some layers not selected');
    return e;
  }, [layers, glassCount]);
  const valid = errs.length === 0;

  const totalPrice = useMemo(() => layers.reduce((s, l) => s + num(l.price), 0), [layers]);
  const totalThk = useMemo(() => layers.reduce((s, l) => s + mmOf(l.product), 0), [layers]);
  // Narration carries each ply's process tags so the collapsed grid row + print
  // read "… WITH TOUGHENED" without expanding the build-up.
  const desc = useMemo(
    () => composeLamiDesc(layers.map((l) => ({ ...l, tags: (l.processPricing || []).map((p) => p.name) }))),
    [layers]);
  const isEdit = row && String(row.type).toUpperCase() === LINE_TYPE.LAMI;

  const handleOk = () => {
    if (!valid || !row) return;
    const lvl = num(row.level) + 1;
    const block = layers.filter((l) => l.product).map((l) => {
      const c = blankLine(1, lvl, row.refUuid);
      c.type = l.layerType === 'GLASS' ? LINE_TYPE.GLASS : LINE_TYPE.INTERLAYER;
      c.itemDesc = l.itemDesc || l.product; c.rawItemDesc = l.rawItemDesc || l.product;
      c.salesRate = num(l.price); c.priceUomCode = row.priceUomCode || null;
      c.gstRate = row.gstRate; c.location = row.location || '';
      c.actualWidthMm = row.actualWidthMm; c.actualHeightMm = row.actualHeightMm; c.qty = row.qty;
      if (l.layerType === 'GLASS') {
        c.itemId = l.itemId || null;
        c.stockItemId = l.stockItemId || null;
        c.stockItemName = l.stockItemName || null;
        c.materialTypeCode = l.materialTypeCode || null;
        c.categoryCode = l.categoryCode || null;
        c.glassTypeCode = l.glassTypeCode || null;
        c.thicknessMm = l.thicknessMm != null ? l.thicknessMm : null;
        c.fabApplicable = l.fabApplicable !== false;
        // Per-ply SHAPE / TEMPLATE opt-in → persisted on THIS ply line's own
        // nesting_config so the Other-Charges engine bills the Shape / Template
        // charge on this ply (same effect as a normal T-SHAPE / T-TEMPLATE line).
        // The parent's nestingConfig.lami.layers[] keeps its own copy for the
        // builder UI restore; this one drives pricing.
        c.nestingConfig = { ...(c.nestingConfig || {}), shape: l.shape === true, template: l.template === true };
        // Per-ply #process@rate/uom tags → the ply line's processPricing; the calc
        // engine folds its valueAddedAmount into this LAMI parent's total + GST.
        const proc = (l.processPricing || []).filter((p) => p.rate > 0);
        if (proc.length) c.processPricing = proc;
      }
      return c;
    });
    onConfirm({
      type: LINE_TYPE.LAMI, itemDesc: desc, rawItemDesc: desc, salesRate: totalPrice,
      isExpanded: false,
      nestingConfig: {
        ...(row.nestingConfig || {}),
        lami: { layers: layers.filter((l) => l.product).map((l) => ({
          layerType: l.layerType, product: l.product, price: num(l.price),
          rawItemDesc: l.rawItemDesc || '', processPricing: l.processPricing || [],
          fabApplicable: l.fabApplicable !== false,
          // SHAPE / TEMPLATE — capture-only per-glass flags (logic: later stage).
          shape: l.layerType === 'GLASS' ? l.shape === true : undefined,
          template: l.layerType === 'GLASS' ? l.template === true : undefined,
          itemId: l.itemId || null, stockItemId: l.stockItemId || null, stockItemName: l.stockItemName || null,
          materialTypeCode: l.materialTypeCode || null, categoryCode: l.categoryCode || null,
          glassTypeCode: l.glassTypeCode || null, thicknessMm: l.thicknessMm != null ? l.thicknessMm : null
        })) }
      }
    }, block);
  };

  return (
    <Modal title={isEdit ? 'Edit Laminated Glass' : 'Build Laminated Glass'}
      open={open} onCancel={onClose} onOk={handleOk} className="gi-nest-modal"
      okText={isEdit ? 'Save Changes' : 'Create Laminate'}
      okButtonProps={{ disabled: !valid, type: 'primary', icon: <SaveOutlined /> }}
      width={820} centered destroyOnClose maskClosable={false}>

      <p className="text-[12px] text-slate-500 -mt-1 mb-3">
        Glass panes bonded by an <b>interlayer</b>. In <b>ITEM DESC</b> type the glass, then add
        <b> #process@rate/uom</b> tags in the same cell (e.g. <span className="font-mono">#Toughened@45/SQF</span>).
        <b> SHAPE</b> / <b>TEMPLATE</b> are captured per glass ply.
      </p>

      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-semibold text-slate-700">Layers (outside → inside)</span>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addPair}>Add glass + film</Button>
      </div>

      {/* ── Excel-style layer grid ── */}
      <table className="gi-lg">
        <colgroup>
          <col className="gi-lg-layer" /><col /><col className="gi-lg-rate" />
          <col className="gi-lg-chk" /><col className="gi-lg-chk" /><col className="gi-lg-tmpl" /><col className="gi-lg-act" />
        </colgroup>
        <thead>
          <tr><th>LAYER</th><th>ITEM DESC</th><th>RATE</th><th>FAB</th><th>SHAPE</th><th>TEMPLATE</th><th aria-label="actions" /></tr>
        </thead>
        <tbody>
          {layers.map((l, i) => {
            const isGlass = l.layerType === 'GLASS';
            return (
              <tr key={i}>
                <td>
                  <Select size="small" style={{ width: '100%' }} value={l.layerType}
                    onChange={(v) => upd(i, v === 'GLASS' ? blankGlass() : blankInter())}
                    options={[{ value: 'GLASS', label: 'GLASS' }, { value: 'INTERLAYER', label: 'INTERLAYER' }]} />
                </td>
                <td>
                  {isGlass
                    ? (
                      <SmartItemDescCell
                        cellId={`lami-desc-${i}`} size="small" dropdowns={dropdowns}
                        row={{ refUuid: `lami-${i}`, rawItemDesc: l.rawItemDesc || '', itemDesc: l.itemDesc || l.product || '', processPricing: l.processPricing || [] }}
                        onCommit={(patch) => upd(i, glassFromRaw(patch.rawItemDesc || '', l))}
                        onPickItem={(item) => {
                          const patch = { ...stockRefOf(item), ...classifyOf(item, item.itemDesc || item.itemName) };
                          if (item.salesRate != null) patch.price = Number(item.salesRate);
                          upd(i, patch);
                        }}
                        onPickCombo={() => {}} onRftAdded={() => {}} />
                    )
                    : (
                      <Select size="small" style={{ width: '100%' }} showSearch value={l.product || undefined} placeholder="Select interlayer"
                        optionFilterProp="label" onChange={(v, opt) => upd(i, { product: v, itemId: (opt && opt.itemId) || null })}
                        options={interOptions} />
                    )}
                </td>
                <td>
                  <InputNumber size="small" style={{ width: '100%' }} min={0} controls={false} prefix="₹"
                    placeholder="rate" value={l.price} onChange={(val) => upd(i, { price: val })} />
                </td>
                <td className="gi-lg-chkcell">
                  {isGlass ? (
                    <Tooltip title="Apply the parent fabrication (holes / cut-outs) to this glass ply. Untick to exclude it from the fabrication count.">
                      <Checkbox checked={l.fabApplicable !== false} onChange={(e) => upd(i, { fabApplicable: e.target.checked })} />
                    </Tooltip>
                  ) : null}
                </td>
                <td className="gi-lg-chkcell">
                  {isGlass ? (
                    <Tooltip title="This glass ply is shaped (non-rectangular). Business logic is defined in a later stage.">
                      <Checkbox checked={l.shape === true} onChange={(e) => upd(i, { shape: e.target.checked })} />
                    </Tooltip>
                  ) : null}
                </td>
                <td className="gi-lg-chkcell">
                  {isGlass ? (
                    <Tooltip title="This glass ply needs a physical template. Business logic is defined in a later stage.">
                      <Checkbox checked={l.template === true} onChange={(e) => upd(i, { template: e.target.checked })} />
                    </Tooltip>
                  ) : null}
                </td>
                <td className="gi-lg-act gi-lg-chkcell">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={layers.length <= 3} onClick={() => removeAt(i)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* validity + totals */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          {valid
            ? <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium"><CheckCircleFilled /> Ready — this is a valid laminate.</span>
            : (
              <ul className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 m-0 list-disc pl-6 space-y-0.5">
                {errs.map((e, k) => <li key={k}>{e}</li>)}
              </ul>
            )}
        </div>
        <div className="flex items-center gap-2.5">
          <Tag color="geekblue" className="!m-0">{glassCount} glass</Tag>
          <Tag color="gold" className="!m-0">{interCount} interlayer</Tag>
          <div className="flex items-baseline gap-1 pl-1">
            <span className="text-[16px] leading-none font-bold text-slate-800 tabular-nums">{totalThk.toFixed(2)}</span>
            <span className="text-[11px] font-semibold text-slate-400">mm</span>
          </div>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1 flex items-baseline gap-1.5">
            <span className="text-[10px] text-emerald-600 uppercase tracking-wide">Rate / area</span>
            <span className="text-[14px] font-bold text-emerald-700 tabular-nums">₹ {totalPrice.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* Full product narration (the line that lands on the proforma row). */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Product narration</div>
        <div className="text-[12.5px] font-medium text-slate-800 leading-snug break-words">{desc || '—'}</div>
      </div>
    </Modal>
  );
}
